/**
 * Date-only arithmetic on 'YYYY-MM-DD' strings, and the plant's working calendar
 * (weekly off days + holidays). Durations in a plan are working days; delays people
 * talk about ("3 days late") are calendar days.
 */

export interface WorkCalendar {
  /** Days of the week that are off: 0 = Sunday … 6 = Saturday. */
  weeklyOff: readonly number[];
  /** Plant holidays (YYYY-MM-DD). */
  holidays: readonly string[];
}

export const DEFAULT_CALENDAR: WorkCalendar = { weeklyOff: [0], holidays: [] };
/** Every day counts (the way the old spreadsheets added days). */
export const CALENDAR_DAYS: WorkCalendar = { weeklyOff: [], holidays: [] };

const DAY_MS = 86_400_000;

function toUtc(date: string): number {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

const fromUtc = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export function addDays(date: string, days: number): string {
  return fromUtc(toUtc(date) + days * DAY_MS);
}

export function isValidDate(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  return addDays(date, 0) === date;
}

/** b − a in calendar days (positive when b is later). */
export function daysBetween(a: string, b: string): number {
  return Math.round((toUtc(b) - toUtc(a)) / DAY_MS);
}

/** 0 = Sunday … 6 = Saturday. */
export const weekday = (date: string): number => new Date(toUtc(date)).getUTCDay();

export const maxDate = (a: string, b: string): string => (a >= b ? a : b);
export const minDate = (a: string, b: string): string => (a <= b ? a : b);

/** A calendar that has at least one working day per week (otherwise nothing could be planned). */
export function safeCalendar(cal: WorkCalendar): WorkCalendar {
  const off = [...new Set(cal.weeklyOff)].filter((d) => d >= 0 && d <= 6);
  return { weeklyOff: off.length >= 7 ? [] : off, holidays: cal.holidays };
}

export function isWorkingDay(date: string, cal: WorkCalendar): boolean {
  return !cal.weeklyOff.includes(weekday(date)) && !cal.holidays.includes(date);
}

/** The date itself when it is a working day, else the next working day. */
export function nextWorkingDay(date: string, cal: WorkCalendar): string {
  const c = safeCalendar(cal);
  let d = date;
  // Bounded: a year of holidays cannot block more than ~400 days.
  for (let i = 0; i < 800 && !isWorkingDay(d, c); i++) d = addDays(d, 1);
  return d;
}

/**
 * `days` working days after `date`. 0 returns the date unchanged (a step that finishes
 * the same day as the one before it). Negative values count backwards.
 */
export function addWorkingDays(date: string, days: number, cal: WorkCalendar): string {
  const c = safeCalendar(cal);
  if (days === 0) return date;
  const step = days > 0 ? 1 : -1;
  let left = Math.abs(days);
  let d = date;
  let guard = 0;
  while (left > 0 && guard++ < 20_000) {
    d = addDays(d, step);
    if (isWorkingDay(d, c)) left -= 1;
  }
  return d;
}

/** Working days from a to b (a exclusive, b inclusive). Negative when b is earlier. */
export function workingDaysBetween(a: string, b: string, cal: WorkCalendar): number {
  const c = safeCalendar(cal);
  if (a === b) return 0;
  const step = a < b ? 1 : -1;
  let n = 0;
  let d = a;
  let guard = 0;
  while (d !== b && guard++ < 20_000) {
    d = addDays(d, step);
    if (isWorkingDay(d, c)) n += step;
  }
  return n;
}
